import React, { useCallback, useEffect, useState } from "react";
import {
    getCompromisosUsuario,
} from "../../../componentes/http/actions/CompromisoAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  TextField,
  CircularProgress,
} from "@mui/material";
import { jwtDecode } from 'jwt-decode';
import { getClientesUsuario } from "../../../componentes/http/actions/UsuarioAction";

const GestorCompromisoUsuario = () => {
  const [compromisos, setCompromisos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Función para obtener compromisos filtrados (sin paginación)
  const getFilteredCompromisos = () => {
    return compromisos.filter((compromiso) => 
      compromiso.descripcion && 
      compromiso.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Compromisos filtrados con paginación
  const filteredCompromisos = getFilteredCompromisos().slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  const fetchCompromisosDeClientes = useCallback(async () => {
    if (clientes.length === 0) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      let todosLosCompromisos = [];
      
      // Obtener compromisos para cada cliente
      for (const cliente of clientes) {
        try {
          const compromisosCliente = await getCompromisosUsuario(token, cliente.id);
          todosLosCompromisos = [...todosLosCompromisos, ...compromisosCliente];
        } catch (error) {
          console.log(`Error al obtener compromisos del cliente ${cliente.nombre}:`, error);
        }
      }
      
      setCompromisos(todosLosCompromisos);
    } catch (error) {
      console.log("Error general al obtener compromisos:", error);
      setSnackbar({
        open: true,
        message: "Error al cargar los compromisos",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setInitialDataLoaded(true);
    }
  }, [clientes]);

  const fetchClientes = useCallback(() => {
    const token = localStorage.getItem('token');
    const decoded = jwtDecode(token);
    const idUsuario = decoded.oid;
    
    setIsLoading(true);
    getClientesUsuario(token, idUsuario)
      .then((response) => {
        setClientes(response);
      })
      .catch((error) => {
        console.log("Error al cargar clientes:", error);
        setSnackbar({
          open: true,
          message: "Error al cargar los clientes",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Cargar datos iniciales
  const loadAllData = useCallback(async () => {
    try {
      await fetchClientes();
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  }, [fetchClientes]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (clientes.length > 0) {
      fetchCompromisosDeClientes();
    }
  }, [clientes, fetchCompromisosDeClientes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const renderMobileCard = (compromiso) => (
    <Card key={compromiso.id} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          Descripción: {compromiso.descripcion}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Normativa: {compromiso.nombreNormativa}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderNoDataMessage = () => (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="body1" color="text.secondary" align="center">
        No se encontraron compromisos con los criterios de búsqueda actuales.
      </Typography>
    </Paper>
  );

  // Si está cargando por primera vez, mostrar solo el spinner
  if (isLoading && !initialDataLoaded) {
    return (
      <Box sx={{ paddingTop: { xs: '20px', sm: '10px' } }}>
        <Typography variant="h4" sx={{ mb: 2 }}>Compromisos</Typography>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: { xs: '20px', sm: '10px' } }}>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        Compromisos
      </Typography>

      {/* Filtro de búsqueda */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Buscar compromiso"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {/* Contenido principal */}
      {filteredCompromisos.length > 0 ? (
        <>
          {isMobile ? (
            <Box>
              {filteredCompromisos.map(renderMobileCard)}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Normativa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompromisos.map((compromiso, index) => (
                    <TableRow key={compromiso.id || index}>
                      <TableCell>{compromiso.descripcion}</TableCell>
                      <TableCell>{compromiso.nombreNormativa}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50]}
            component="div"
            count={getFilteredCompromisos().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : initialDataLoaded ? (
        renderNoDataMessage()
      ) : null}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GestorCompromisoUsuario;